// utilities/requestTypes.js
//
// PURE helpers for the request-type admin (/settings/request-types, SOUND-544).
// Mirrors the API rules in sound-api/utilities/ticketRequestTypes.mjs so the
// editor can refuse a bad key before the round trip; the API remains the
// enforcement point.

export const REQUEST_TYPE_PRIORITIES = ["low", "normal", "high", "urgent"];

/** lowercase snake_case: starts with a letter, then a-z / 0-9 / _, max 64. */
export function isValidRequestTypeKey(key) {
  return typeof key === "string" && /^[a-z][a-z0-9_]*$/.test(key) && key.length <= 64;
}

/** A label → a suggested key ("Cap Increase (AMEX)" → "cap_increase_amex"). */
export function suggestRequestTypeKey(label) {
  const s = String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[0-9_]+/, "");
  return s.slice(0, 64).replace(/_+$/, "");
}

/** audience[] ⇄ the editor's two-way choice. */
export function audienceChoiceOf(audience) {
  return Array.isArray(audience) && audience.includes("client") ? "internal_client" : "internal";
}
export function audienceFromChoice(choice) {
  return choice === "internal_client" ? ["internal", "client"] : ["internal"];
}

/** Editor draft from an API row (or a blank create draft). */
export function requestTypeDraft(rt) {
  return {
    key: rt?.key || "",
    label: rt?.label || "",
    description: rt?.description || "",
    defaultPriority: rt?.defaultPriority || "",
    audience: audienceChoiceOf(rt?.audience),
    focus: !!rt?.focus,
  };
}

/** Client-side issues before save. */
export function requestTypeIssues(draft, { isNew, existingKeys = [] } = {}) {
  const out = [];
  if (isNew) {
    if (!draft.key) out.push("Key is required.");
    else if (!isValidRequestTypeKey(draft.key))
      out.push("Key must be lowercase snake_case: starts with a letter, then a–z, 0–9 or _ (max 64).");
    else if (existingKeys.includes(draft.key)) out.push(`Key "${draft.key}" already exists.`);
  }
  if (!draft.label.trim()) out.push("Label is required.");
  else if (draft.label.trim().length > 120) out.push("Label must be at most 120 characters.");
  if ((draft.description || "").trim().length > 2000) out.push("Description must be at most 2000 characters.");
  return out;
}

/**
 * The request body. Create sends the key; update never does (the API 400s a
 * changed key and refuses `active`). Blank priority/description → null.
 */
export function requestTypeBody(draft, { isNew }) {
  const body = {
    label: draft.label.trim(),
    description: (draft.description || "").trim() || null,
    defaultPriority: draft.defaultPriority || null,
    audience: audienceFromChoice(draft.audience),
    focus: !!draft.focus,
  };
  return isNew ? { key: draft.key, ...body } : body;
}

const DEPENDANT_KIND_LABELS = {
  telegram_command: "Telegram command",
  sla_policy: "SLA policy",
};

/**
 * Read a request-type write failure into something the page can show in full.
 * Never swallows: an unrecognised body still yields its `error` string.
 * @returns {{title: string, message: string, dependants: Array<{kind, kindLabel, id, name}>}}
 */
export function describeRequestTypeError(err, fallbackTitle = "Request failed") {
  const status = err?.response?.status;
  const body = err?.response?.data || {};
  const message = body.error || err?.message || "Unknown error";
  const dependants = Array.isArray(body.dependants)
    ? body.dependants.map((d) => ({
        kind: d?.kind || "unknown",
        kindLabel: DEPENDANT_KIND_LABELS[d?.kind] || d?.kind || "Dependant",
        id: d?.id ?? null,
        name: d?.name || d?.id || "(unnamed)",
      }))
    : [];
  let title = fallbackTitle;
  if (body.code === "REQUEST_TYPE_HAS_DEPENDANTS") title = "Still in use";
  else if (body.code === "REQUEST_TYPE_UNDEACTIVATABLE") title = "Cannot be deactivated";
  else if (status === 409) title = "Conflict";
  return { title, message, dependants };
}

/** List ordering: active first, then label. */
export function sortRequestTypes(rows = []) {
  return [...rows].sort((a, b) => {
    const aa = a.active === false ? 1 : 0;
    const bb = b.active === false ? 1 : 0;
    if (aa !== bb) return aa - bb;
    return String(a.label || a.key).localeCompare(String(b.label || b.key));
  });
}

function humanizeKey(key) {
  return String(key || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * A key → label resolver for INTERNAL settings screens (SOUND-785 ride-along).
 *
 * Feed it the admin vocabulary (`/ticket-request-types?includeInactive=true`),
 * not `/tickets/request-types`: the latter omits deactivated types, so a
 * workflow or SLA policy on a since-deactivated type rendered as a raw key.
 * A deactivated type's label gains " (deactivated)" unless `markInactive` is
 * false; an unknown key is humanised rather than shown raw.
 */
export function requestTypeLabeler(rows = [], { markInactive = true } = {}) {
  const map = new Map((rows || []).map((rt) => [rt.key, rt]));
  return (key) => {
    if (!key) return "";
    const rt = map.get(key);
    if (!rt) return humanizeKey(key);
    const label = rt.label || humanizeKey(key);
    return markInactive && rt.active === false ? `${label} (deactivated)` : label;
  };
}

/**
 * Options for a request-type <select> on a settings editor: every ACTIVE type,
 * plus the CURRENT value when it is a deactivated (or unknown) type — otherwise
 * the select would silently show a different option than the record holds.
 * @returns {Array<{key: string, label: string, active: boolean}>}
 */
export function requestTypeSelectOptions(rows = [], currentKey = "") {
  const label = requestTypeLabeler(rows);
  const active = sortRequestTypes((rows || []).filter((rt) => rt.active !== false)).map((rt) => ({
    key: rt.key,
    label: rt.label || rt.key,
    active: true,
  }));
  if (currentKey && !active.some((o) => o.key === currentKey)) {
    active.push({ key: currentKey, label: label(currentKey), active: false });
  }
  return active;
}
